import * as React from 'react';

import { useFunctionInBulk } from '@notino/react-web-utils';
import { Product } from '@notino/web-tracking/dist/types/package/ga4/events/products';

import { dispatchTrackingEvent } from '@context/tracking/utils';

type InteractionResolver = (info: { isInitial: boolean }) => string;

interface ITrackDynamicListArgs {
  delay?: number;
  /**
   * Value of `product_lists.interaction`. Pass a resolver to distinguish the
   * initial (no user interaction) impressions from later ones triggered by
   * scrolling/swiping - it receives `isInitial: true` only for the first
   * dispatched batch.
   */
  interaction?: string | InteractionResolver;
  listName: string;
}

export type TrackedProduct = Product & { list_position: number; id: string };

export const useTrackDynamicList = ({
  delay = 500,
  interaction = 'scroll',
  ...rest
}: ITrackDynamicListArgs) => {
  const hasDispatchedRef = React.useRef(false);

  const trackProductsImpression = React.useCallback(
    (products: TrackedProduct[], params: ITrackDynamicListArgs) => {
      const isInitial = !hasDispatchedRef.current;
      hasDispatchedRef.current = true;

      dispatchTrackingEvent({
        event: 'view_item_list',
        product_lists: {
          products,
          interaction:
            typeof interaction === 'function'
              ? interaction({ isInitial })
              : interaction,
          sorting_type: 'automatic',
          list_id: params.listName,
          list_name: params.listName,
        },
        _clear: true,
      });
    },
    [interaction]
  );

  return useFunctionInBulk<
    TrackedProduct,
    // @ts-ignore
    Omit<ITrackDynamicListArgs, 'delay'>
  >(trackProductsImpression, delay, rest);
};
